import { Component, Input, OnInit } from '@angular/core';
import {
	ComponentBase,
	SearchService,
	WebAnalyticsService,
	DeploymentContext,
	Summary,
} from '@Common';
import { LocalizedTextIds, Mode } from 'company-finder-common';

enum DrilldownType {
	Count,
	Sector,
	CrossSector,
	New,
	Status,
	BlueKnight,
	QfcWinner,
}

@Component({
	selector: 'summary',
	templateUrl: './summary.component.html',
	styleUrls: ['./summary.component.scss'],
	standalone: false,
})
export class SummaryComponent extends ComponentBase implements OnInit {
	public comprehensiveSummary: Summary;
	@Input()
	public summary: Summary;

	private _mode: Mode = Mode.Company;
	public get mode(): Mode {
		return this._mode;
	}

	@Input()
	public set mode(value: Mode) {
		this._mode = value;
	}

	public isShowingTotalTooltip = false;
	public isShowingTileTooltip = false;
	public DrilldownTypes = DrilldownType;
	public DrilldownType: DrilldownType = DrilldownType.Count;

	public constructor(
		dc: DeploymentContext,
		private _searchService: SearchService,
		private _webAnalyticsService: WebAnalyticsService
	) {
		super(dc);
	}

	async ngOnInit(): Promise<void> {
		this.comprehensiveSummary =
			this._deploymentContext.comprehensiveSummary;
	}

	public get currentCompanyCountDisplay(): string {
		return `${this.summary.numberOfCompanies}`;
	}

	public get totalCompaniesCount(): number {
		return this.comprehensiveSummary?.numberOfCompanies;
	}

	public get titleLabel(): string {
		return this.Loc(LocalizedTextIds.SummaryJLABSCompanies);
	}

	public get totalCompaniesCountDisplay(): string {
		return `${this.totalCompaniesCount}`;
	}

	public get showingAllCompanies(): boolean {
		if (this.summary.numberOfCompanies === this.totalCompaniesCount) {
			return true;
		}
		return;
	}

	public get shouldShowSummary(): boolean {
		return (
			this.summary &&
			(this.featureSwitches.enableSectors ||
				this.shouldShowBlueKnightCount ||
				this.shouldShowQfcCount)
		);
	}

	public get shouldShowNewCompanyCount(): boolean {
		return (
			this.featureSwitches.enableShowNewCompanies &&
			!this.shouldShowBlueKnightCount &&
			this.comprehensiveSummary &&
			this.comprehensiveSummary.numberOfNewCompanies > 0
		);
	}

	public get shouldShowShowStatusCount(): boolean {
		// ADJQ-1946 Prior to 11/25 this was based on the enableShowLocationStatus
		// flag (which is on for JLabs, off for Universal) which drives
		// filter dropdowns and showing status for a company. With assets,
		// Universal now has a status tile but no other statuses, so
		// it is decoupled from that flag and enabled wherever data permits
		return this._deploymentContext.statusesForTile?.length > 0;
	}

	public get numberOfResidentsForStatusTile(): number {
		return this.summary.numberOfResidentsForStatusTile;
	}

	public get shouldShowQfcCount(): boolean {
		return this.featureSwitches.enableQfc;
	}

	public get shouldShowBlueKnightCount(): boolean {
		return (
			this.featureSwitches.enableBlueKnight &&
			this.comprehensiveSummary?.numberOfBlueKnights > 0
		);
	}

	public get shouldShowRightSeparator(): boolean {
		return (
			this.inCompanyMode &&
			(this.shouldShowNewCompanyCount ||
				this.shouldShowBlueKnightCount ||
				this.shouldShowQfcCount ||
				this.shouldShowShowStatusCount)
		);
	}

	public get shouldShowCrossSector(): boolean {
		return this.summary.numberOfCrossSectorCompanies > 0;
	}

	public get shouldShowRightContainer(): boolean {
		return (
			this.inCompanyMode &&
			(this.shouldShowNewCompanyCount ||
				this.shouldShowShowStatusCount ||
				this.shouldShowQfcCount ||
				this.shouldShowBlueKnightCount)
		);
	}

	public showTotalTooltip(): void {
		this.isShowingTotalTooltip = this.totalToolipText.length > 0;
	}

	public hideTotalTooltip(): void {
		this.isShowingTotalTooltip = false;
	}

	public get totalToolipText() {
		return this.Loc(LocalizedTextIds.SummaryGlad);
	}

	public showTileTooltip(): void {
		this.isShowingTileTooltip =
			this._deploymentContext.tileTooltip?.length > 0;
	}

	public hideTileTooltip(): void {
		this.isShowingTileTooltip = false;
	}
	public get tileLabel(): string {
		return this._deploymentContext.tileLabel;
	}

	public get tileTooltip(): string {
		return this._deploymentContext.tileTooltip;
	}

	public async handleClick(
		drilldownType: DrilldownType,
		sector: string = null
	): Promise<void> {
		// FUTURE: For now, this component only ever shows Company stats, and this can be visible on the Overview when the active mode is "Asset".
		//	So, for now, just explicitly index into the companyFilter.
		const filter = this._searchService.companyFilter;
		switch (drilldownType) {
			case DrilldownType.Count:
				// FUTURE: This was a quick hack related to ADJQ-210.  There could definitely be a cleaner way of
				// indicating that we should *always* transition to the search results in this case.
				this._searchService.currentSearchPredicate = ' ';
				break;
			case DrilldownType.Sector:
				filter.primarySectors = [sector];
				break;
			case DrilldownType.CrossSector:
				filter.isCrossSector = true;
				break;
			case DrilldownType.New:
				filter.isNewInLastQuarter = true;
				break;
			case DrilldownType.Status: {
				const locationStatuses = [];
				const companyStatuses = [];
				for (const status of this._deploymentContext.statusesForTile) {
					const statusMeta =
						this._deploymentContext.statusMetadataCollection.getMetadata(
							status
						);
					if (statusMeta) {
						switch (statusMeta.statusType) {
							case 'location':
								locationStatuses.push(status);
								break;
							case 'company':
								companyStatuses.push(status);
								break;
						}
					}
				}

				filter.locationStatuses = locationStatuses.join();
				filter.companyStatuses = companyStatuses.join();

				break;
			}
			case DrilldownType.BlueKnight:
				filter.isBlueKnight = true;
				break;
			case DrilldownType.QfcWinner:
				filter.isQFCWinner = true;
				break;
		}

		this._searchService.drilldownSubject.next(Mode.Company);

		this._webAnalyticsService.trackEvent('summary-drilldown', {
			label:
				drilldownType === DrilldownType.Sector
					? sector
					: DrilldownType[drilldownType],
			// value: stageCount.count // FUTURE: There is no easy way to get this without replicating display logic
		});
	}
}
